import React, { useEffect, Fragment } from 'react'
import moment from 'moment'
import { Formik, Form } from 'formik'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import * as currentUserActionsCreators from '../../redux/currentUserManager/currentUserManager'
import * as userManagerOperations from '../../redux/css-admin/userManager/operations'
import * as notificationActionsCreators from '../../redux/notifications'
import api from '../../lib/Api'
import { domains } from 'lib/config'
import { jobRolesLabel, jobRoles, schoolLookupLabel } from 'lib/meta'
import { userProfileSchema } from 'lib/schemas'
import AccountDetailsFirstTimeUserAndPromptToCompleteHeader from 'components/AccountDetailsFirstTimeUserAndPromptToCompleteHeader/AccountDetailsFirstTimeUserAndPromptToCompleteHeader'
import AccountDetailsFormikController from 'components/AccountDetailsFormikController'
import ErrorBoundary from 'components/ErrorBoundary'
import SchoolLookUp from 'components/SchoolLookUp'
import RegisteredTrademarkIcon from 'components/RegisteredTrademarkIcon'
import {
  Container,
  ErrorContainer,
  Header,
  Required,
  SchoolLookUpContainer,
  StyledHr,
  SubmitButton,
  SubmitSection,
} from './EditProfiles.styles'

const EditProfileForm = props => {
  const defaultProps = {
    profileTtlMin: 5,
  }
  const { addNotification, currentUser } = props
  const pathname = location.pathname
  const isFirstTimeUser = pathname === '/account-details/first-time'
  const promptToComplete = pathname === '/account-details/prompt-to-complete'
  const isSSO = currentUser.isSSO
  const buttonText = isFirstTimeUser || promptToComplete ? 'Finish' : 'Save'
  
  const initialValues = currentUser.hasInactiveNetSuiteCustomer ?
    { ...currentUser, 
      companyName: '',
      region: '',
      city: '', 
      companyName: '', 
      country: '', 
      nsCustomerId: '',
      netSuiteCustomerId: ''
    } : currentUser

  useEffect(() => {
    currentUser.archetypes = promptToComplete
      ? []
      : currentUser.archetypes.map(archetype => archetype.id)
    const fetchDate = moment(props.currentUserFetchedAt)
    const refetchDate = fetchDate.add(
      props.profileTtlMin || defaultProps.profileTtlMin,
      'm',
    )
    if (moment() > refetchDate) {
      props.fetchCurrentUserAndHandler()
    }
    const doesJobRoleExist = jobRoles.some(
      jobRole => jobRole.id === currentUser.title,
    )
    if ((currentUser.title && !doesJobRoleExist) || promptToComplete) {
      currentUser.title = ''
    }

    props.fetchAndHandleArchetypes()
  }, [])

  const handleSchoolChange = (details, setFieldValue) => {
    setFieldValue('country', details.country)
    setFieldValue('city', details.city)
    setFieldValue('region', details.region)
    setFieldValue('companyName', details.companyName)
    setFieldValue('nsCustomerId', details.nsCustomerId)
    setFieldValue('netsuiteCustomerId', details.netsuiteCustomerId)
    setFieldValue('organizationName', details.organizationName)
    setFieldValue('siteId', details.siteId)
  }

  const handleSubmit = async (values, actions) => {
    if (values.netsuiteCustomerId === 0)
      values.siteName = values.organizationName

    delete values.promptToComplete

    const reqData = {
      id: values.id,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      title: values.title,
      mobilePhoneNumber: values.mobilePhoneNumber || null,
      contactPhoneNumber: values.contactPhoneNumber || null,
      siteName: values.companyName || values.organizationName,
      netsuiteCustomerId: values.netsuiteCustomerId,
      country: values.country,
      region: values.region || null,
      city: values.city,
      archetypes: values.archetypes || [],
    }

    await api
      .put('/profile/me', reqData)
      .then(() => {
        actions.setSubmitting(false)
        actions.resetForm(values)
        addNotification({
          color: 'success',
          text: 'Successfully updated profile',
        })
        if (isFirstTimeUser || promptToComplete) {
          window.location = domains.CURRICULUM_APP_DOMAIN
        }
      })
      .catch(error => {
        actions.setSubmitting(false)
        const { response } = error
        const { data = '' } = response || {}
        const text = !data ? 'Failed to update profile' : data
        addNotification({
          color: 'danger',
          text: text,
        })
      })
  }

  return (
    <ErrorBoundary>
      <Container>
        {isFirstTimeUser || promptToComplete ? (
          <AccountDetailsFirstTimeUserAndPromptToCompleteHeader
            isFirstTimeUser={isFirstTimeUser}
            promptToComplete={promptToComplete}
          />
        ) : (
          <Header>Account Details</Header>
        )}
        <StyledHr />
        <Required>* Required</Required>
        {currentUser && (
          <Formik
            enableReinitialize={true}
            initialValues={Object.assign( initialValues , { promptToComplete })}
            onSubmit={handleSubmit}
            validationSchema={userProfileSchema}
          >
            {formikProps => {
              const { values, errors, dirty, setFieldValue } = formikProps
              const isValid =
                !Object.keys(errors).length &&
                ((values.firstName && values.lastName) || values.isSSO) &&
                values.title &&
                values.archetypes.length > 0 &&
                values.country &&
                values.city &&
                (values.organizationName || values.companyName !== '')
              return (
                <Form>
                  {!(isFirstTimeUser || promptToComplete) && (
                    <AccountDetailsFormikController
                      control="userDetails"
                      ErrorContainer={ErrorContainer}
                      errors={errors}
                      isSSO={isSSO}
                      names={['email', 'firstName', 'lastName']}
                      values={[values.email, values.firstName, values.lastName]}
                    />
                  )}
                  <AccountDetailsFormikController
                    control="radio"
                    ErrorContainer={ErrorContainer}
                    errors={errors.title}
                    jobRoles={jobRoles}
                    label={jobRolesLabel}
                    name="title"
                    value={values.title}
                  />
                  <AccountDetailsFormikController
                    archetypesData={props.archetypes}
                    control="checkbox"
                    ErrorContainer={ErrorContainer}
                    errors={errors.archetypes}
                    label={
                      <Fragment>
                        How are you involved with Second Step
                        <RegisteredTrademarkIcon /> programs? Select all that
                        apply:*
                      </Fragment>
                    }
                    name="archetypes"
                    setFieldValue={setFieldValue}
                    values={values.archetypes}
                  />
                  {!(
                    promptToComplete &&
                    currentUser.city &&
                    currentUser.country &&
                    currentUser.companyName
                  ) && (
                    <SchoolLookUpContainer>
                      <label>{schoolLookupLabel}</label>
                      <SchoolLookUp
                        fireOnLoad={false}
                        onSelection={details =>
                          handleSchoolChange(details, setFieldValue)
                        }
                        siteData= {{ 
                          city: values.city || '',
                          country: values.country || '',
                          companyName: values.companyName || '',
                          netsuiteCustomerId: values.netsuiteCustomerId || null,
                          nsCustomerId: values.nsCustomerId || '',
                          organizationName: values.organizationName || '',
                          region: values.region || '',
                        }}
                      />
                    </SchoolLookUpContainer>
                  )}
                  <SubmitSection
                    isFirstTimeUser={isFirstTimeUser || promptToComplete}
                  >
                    <SubmitButton
                      disabled={
                        promptToComplete || isFirstTimeUser
                          ? !isValid
                          : !(isValid && dirty)
                      }
                      type="submit"
                    >
                      {buttonText}
                    </SubmitButton>
                  </SubmitSection>
                </Form>
              )
            }}
          </Formik>
        )}
      </Container>
    </ErrorBoundary>
  )
}

function mapStateToProps({ currentUserManager, userManager }) {
  return {
    ...currentUserManager,
    archetypes: userManager.archetypes.data,
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      ...currentUserActionsCreators,
      ...userManagerOperations,
      ...notificationActionsCreators,
    },
    dispatch,
  )
}
export default connect(mapStateToProps, mapDispatchToProps)(EditProfileForm)



// WEBPACK FOOTER //
// ./src/containers/account/EditProfileForm.js